import { ChangeDetectionStrategy, Component, inject, input, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { CanViewPageDirective } from '../../../auth/directives/can-view-page.directive';
import { HideForOnboardingDirective } from '../../../auth/directives/hide-for-onboarding.directive';
import { UserStore } from 'app/core/services/user.store';
import { ModulesStore } from '../../../../features/admin/modules/modules.store';
import { BILLING_PAGE_CODE } from '../../../../features/admin/modules/modules-pages/templates/billing/billing.types';

@Component({
  selector: 'user',
  template: `
    <!-- Button -->
    @if (userStore.currentUser(); as user) {
      <button mat-icon-button [matMenuTriggerFor]="userActions" data-cy="user-menu">
        <span class="relative">
          @if (!showAvatar()) {
            <mat-icon [matTooltip]="'Gebruikersprofiel'" [svgIcon]="'feather:user'"></mat-icon>
          }
        </span>
      </button>

      <mat-menu [xPosition]="'before'" #userActions="matMenu">
        <div class="px-4 py-2">
          <span class="text-sm flex flex-col leading-none">
            <span>Ingelogd als</span>
            <span class="mt-1.5 font-bold" data-cy="logged-in-user">{{ user.name }}</span>
            @if (userStore.emailAddress(); as email) {
              <span class="mt-1 text-xs text-accent truncate">{{ email }}</span>
            }
          </span>
        </div>

        <ng-container *hideForOnboarding>
          <ng-container *canViewPage="{ pageId: profilePageId, show: true }">
            <mat-divider></mat-divider>
            <a mat-menu-item routerLink="/profiel" data-cy="user-menu_profile-button">
              <mat-icon [svgIcon]="'feather:user'"></mat-icon>
              <span>Profiel</span>
            </a>
          </ng-container>
          <ng-container *canViewPage="{ pageId: companyPageId, show: true }">
            <mat-divider></mat-divider>
            <button mat-menu-item routerLink="/bedrijfsinstellingen" data-cy="user-menu_company-button">
              <mat-icon [svgIcon]="'mat_outline:business'"></mat-icon>
              <span>Bedrijfsinstellingen</span>
            </button>
          </ng-container>
          <ng-container *canViewPage="{ pageId: billingPageId, show: true }">
            <mat-divider></mat-divider>
            <a mat-menu-item routerLink="/facturatie" data-cy="user-menu_billing-button">
              <mat-icon [svgIcon]="'mat_outline:receipt_long'"></mat-icon>
              <span>Facturatie</span>
            </a>
          </ng-container>
          <!-- Instellingen also lives behind the onboarding gate: AuthGuard sends
               every slug except /onboarding back, so it would be a dead link. -->
          <ng-container *canViewPage="{ pageId: usersettingsPageId, show: true }">
            <mat-divider></mat-divider>
            <a mat-menu-item routerLink="/gebruikersinstellingen" data-cy="user-menu_usersettings-button">
              <mat-icon [svgIcon]="'feather:settings'"></mat-icon>
              <span>Instellingen</span>
            </a>
          </ng-container>
        </ng-container>
        <mat-divider></mat-divider>
        <button mat-menu-item (click)="signOut()" data-cy="user-menu_logout-button">
          <mat-icon [svgIcon]="'feather:log-out'"></mat-icon>
          <span>Uitloggen</span>
        </button>
      </mat-menu>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'user',
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    MatTooltip,
    RouterLink,
    CanViewPageDirective,
    HideForOnboardingDirective,
  ],
})
export class UserComponent {
  readonly showAvatar = input(false);

  readonly userStore = inject(UserStore);
  readonly modulesStore = inject(ModulesStore);
  readonly router = inject(Router);

  readonly profilePageId = this.modulesStore.moduleCodeToId('profile');
  readonly companyPageId = this.modulesStore.moduleCodeToId('company-settings');
  readonly usersettingsPageId = this.modulesStore.moduleCodeToId('usersettings');
  readonly billingPageId = this.modulesStore.moduleCodeToId(BILLING_PAGE_CODE);

  viewUserAccount(id: string) {
    this.router.navigate(['/personeel/' + id]);
  }

  /**
   * Sign out
   */
  signOut(): void {
    this.router.navigate(['/sign-out']);
  }
}
